import { useDispatch, useSelector } from 'react-redux';
import {
  putMultipleFundingSourceForClientByClientIdRequest,
  selectClient,
  selectFeatureFlags,
} from '../model';
import {
  FundingPolicyType,
  calculateAgeFromBirthDate,
  formatCurrency,
  fundingPolicyTypeDefs,
  getGroupPolicyPremiumRate,
  isNullOrUndefined,
} from '@shared';
import { useState } from 'react';

import { InsuranceState, InsuranceWhiteBoxSlideOutContent, SlideOutSheet } from '..';
import { INSURANCE_SHOPPING_CONFIG } from '@client/util/constants';
import { Radio, RadioGroup } from '@headlessui/react';
import { Button } from './ui/button';
import { HeartHandshake, Headphones } from 'lucide-react';
// import { PolicyInsight } from '@/components/policy-insight/PolicyInsight';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

type GroupInsurancePlanSelectorProps = {
  className?: string;
  setInsuranceState: (insuranceState: InsuranceState) => void;
};

type LtcPolicyConfig = {
  policyType: FundingPolicyType;
  benefitAmountOptions: string[];
  getBenefitAmountPlans: (
    clientSmokedRecently: string,
    clientAge: number,
    options: string[],
  ) => BenefitAmountPlan[];
  handlePolicyUpdate: (
    value: number,
    policyData: any,
    clientHasStartedLtc: boolean,
    clientSmokedRecently: string,
    clientAge: number,
  ) => PolicyFundingSource;
};

export const agisEnrollmentUrl = 'https://xyztlc.enrollment.caregivingexchange.com/TLC';
export const agisAppointmentUrl = 'https://navigatorngltest.caregivingexchange.com/MakeAppointment';

export function GroupInsurancePlanSelector({
  className,
  setInsuranceState,
}: GroupInsurancePlanSelectorProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const featureFlags = useSelector(selectFeatureFlags);
  const {
    clientId,
    multipleFundingSources: { ltcPolicy },
    intakeSurvey: { clientHasStartedLtc, clientAge, clientSmokedRecently, clientBirthDate },
  } = client;

  // Define policy configurations based on feature flags
  const policyConfigs: Record<string, LtcPolicyConfig> = {
    vbShop: {
      policyType: FundingPolicyType.allstateHybridPolicy,
      benefitAmountOptions: ['50000', '100000', '150000', '200000', '250000', '0'],
      getBenefitAmountPlans: (clientSmokedRecently, clientAge, options) =>
        options.map(amount => {
          const premiumRate = getGroupPolicyPremiumRate(
            clientSmokedRecently === 'Yes',
            clientAge,
            amount,
            'vbShop',
          );
          return {
            benefitAmount: Number.parseInt(amount),
            premiumMonthly: premiumRate ?? 0,
          };
        }),
      handlePolicyUpdate: (
        value,
        policyData,
        clientHasStartedLtc,
        clientSmokedRecently,
        clientAge,
      ) => ({
        ...policyData,
        policyType: FundingPolicyType.allstateHybridPolicy,
        policyPremiumStartYear: new Date().getFullYear(),
        policyInflationProtection: 0.05,
        homeCareWaitingPeriodDays: 90,
        facilityCareWaitingPeriodDays: 90,
        isIndemnityPolicyPayment: true,
        customPolicyId: value.toString(),
        policyBenefitUtilizedToDate: clientHasStartedLtc ? 0 : null,
        policyContinuationBenefitAmount: value,
        policyMaximumBenefitAmount: value,
        policyMinimumGuaranteedBenefitAmount: value,
        dailyHomeBenefitAmount: (value * 0.06) / 30,
        dailyIndependentLivingBenefitAmount: (value * 0.06) / 30,
        dailyAssistedLivingBenefitAmount: (value * 0.06) / 30,
        dailyNursingHomeBenefitAmount: (value * 0.06) / 30,
        hasPolicyFunding: true,
        policyPremiumMonthlyCost:
          getGroupPolicyPremiumRate(
            clientSmokedRecently === 'Yes',
            clientAge,
            value.toString(),
            'vbShop',
          ) ?? 0,
      }),
    },
    agis: {
      policyType: FundingPolicyType.agisGroupPolicy,
      benefitAmountOptions: ['50000', '100000', '150000', '200000', '0'],
      getBenefitAmountPlans: (clientSmokedRecently, clientAge, options) =>
        options.map(amount => {
          // AGIS might have a different premium calculation
          const premiumRate = getGroupPolicyPremiumRate(
            clientSmokedRecently === 'Yes',
            calculateAgeFromBirthDate(clientBirthDate, true),
            amount,
            'agis',
          );
          return {
            benefitAmount: Number.parseInt(amount),
            premiumMonthly: premiumRate ?? 0,
          };
        }),
      handlePolicyUpdate: (
        value,
        policyData,
        clientHasStartedLtc,
        clientSmokedRecently,
        clientAge,
      ) => ({
        ...policyData,
        policyType: FundingPolicyType.agisGroupPolicy,
        policyPremiumStartYear: new Date().getFullYear(),
        policyInflationProtection: 0,
        homeCareWaitingPeriodDays: 90, // Different for AGIS
        facilityCareWaitingPeriodDays: 90, // Different for AGIS
        isIndemnityPolicyPayment: true,
        customPolicyId: value.toString(),
        policyBenefitUtilizedToDate: clientHasStartedLtc ? 0 : null,
        policyContinuationBenefitAmount: value * 2,
        policyMaximumBenefitAmount: 0,
        policyMinimumGuaranteedBenefitAmount: value,
        dailyHomeBenefitAmount: (value * 0.04) / 30, // Different for AGIS
        dailyIndependentLivingBenefitAmount: (value * 0.04) / 30, // Different for AGIS
        dailyAssistedLivingBenefitAmount: (value * 0.04) / 30, // Different for AGIS
        dailyNursingHomeBenefitAmount: (value * 0.04) / 30, // Different for AGIS
        hasPolicyFunding: true,
        policyPremiumMonthlyCost:
          getGroupPolicyPremiumRate(
            clientSmokedRecently === 'Yes',
            calculateAgeFromBirthDate(clientBirthDate, true),
            value.toString(),
            'agis',
          ) ?? 0,
      }),
    },
  };

  // Determine which policy config to use based on feature flags
  const activePolicyConfig = featureFlags.vbShop
    ? policyConfigs.vbShop
    : featureFlags.agis
      ? policyConfigs.agis
      : null;

  // Find the appropriate policy based on the active policy type
  const [fundingSourceId, ltcGroupPolicy] = Object.entries(ltcPolicy).find(
    ([_key, value]) => value.policyType === activePolicyConfig?.policyType,
  ) ?? [null, null];

  const [selectedBenefitAmount, setSelectedBenefitAmount] = useState<number | undefined>(
    isNullOrUndefined(ltcGroupPolicy?.customPolicyId)
      ? undefined
      : Number.parseInt(ltcGroupPolicy?.customPolicyId!),
  );

  const benefitAmountOptions = activePolicyConfig?.benefitAmountOptions;

  const benefitAmountPlans = activePolicyConfig?.getBenefitAmountPlans(
    clientSmokedRecently,
    clientAge,
    benefitAmountOptions ?? [],
  );

  const handleChangeSelectedBenefitAmount = (value: number) => {
    setSelectedBenefitAmount(value);
    const policyFundingSource = activePolicyConfig?.handlePolicyUpdate(
      value,
      ltcGroupPolicy,
      clientHasStartedLtc,
      clientSmokedRecently,
      clientAge,
    );

    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: policyFundingSource ?? null,
        fundingSourceId,
      }),
    );
  };

  if (!activePolicyConfig) {
    return null;
  }

  const agisAgeLimit = INSURANCE_SHOPPING_CONFIG.groupPolicyAge.agisMax;
  const isAgisIneligible = featureFlags.agis && (clientHasStartedLtc || clientAge > agisAgeLimit);

  if (isAgisIneligible) {
    return (
      <div
        className={classNames(
          className ?? '',
          'border-primary-200 bg-primary-50 flex w-full flex-col gap-2 rounded-lg border p-6',
        )}
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="bg-primary-100 rounded-full p-4 text-gray-500">
            <HeartHandshake className="text-primary-dark h-6 w-6 md:h-8 md:w-8" />
          </div>
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-primary-dark text-lg font-medium md:text-xl">
              We're sorry, this coverage isn't available
            </h2>
            <p className="max-w-lg text-sm text-gray-600 md:text-base">
              Trustmark Life + Care Insurance is only available for individuals under {agisAgeLimit}{' '}
              who haven't started long-term care. We'd be happy to discuss alternative options that
              might better suit your needs.
            </p>

            <ContactLtcSpecialistButton
              featureFlags={featureFlags}
              buttonClassName="my-3 max-w-xs md:my-4"
              iconClassName="mr-2 h-3 w-3 md:h-4 md:w-4"
              variant="outline"
              children="Speak with an LTC Specialist"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(className ?? '', 'flex w-full flex-col gap-2')}>
      <div className="flex flex-col items-start justify-between gap-4 pb-6 md:flex-row md:gap-0">
        <div className="ml-1 space-y-1">
          <h2 className="text-xl font-medium leading-5 text-gray-800">
            {fundingPolicyTypeDefs[activePolicyConfig.policyType]?.label ?? 'Group LTC Insurance'}
          </h2>
          <p className="text-base font-light text-gray-700 md:text-lg">Select a benefit amount.</p>
        </div>
        <div className="flex w-full flex-col gap-2 text-gray-800 hover:text-gray-800 md:w-auto md:flex-row">
          <ContactLtcSpecialistButton
            featureFlags={featureFlags}
            iconClassName="h-6 w-6"
            children="Talk to an LTC Specialist"
          />
        </div>
      </div>

      <PlanSelectionRadioOptions
        plans={benefitAmountPlans ?? []}
        selectedBenefitAmount={selectedBenefitAmount}
        setSelectedBenefitAmount={handleChangeSelectedBenefitAmount}
      />

      {fundingSourceId && (
        <div className="flex w-full flex-row-reverse pt-4 ">
          <div className="flex flex-col items-end">
            <Button
              variant="link"
              onClick={() =>
                setInsuranceState({
                  selectedPolicyFundingSourceId: fundingSourceId,
                  selectedPolicyType: activePolicyConfig.policyType,
                  openInsuranceDetailsModal: true,
                })
              }
            >
              <span className="text-primary-dark text-sm font-medium">View plan details</span>
            </Button>

            <SlideOutSheet
              title="Policy Insights"
              // slideOutContent={<PolicyInsight fundingSourceId={fundingSourceId} />}
              slideOutContent={
                <InsuranceWhiteBoxSlideOutContent fundingSourceId={fundingSourceId} />
              }
              maxWidth="max-w-2xl"
              // noMargin
            >
              <Button variant="link">
                <span className="text-primary-dark text-sm font-medium">Explain my coverage</span>
              </Button>
            </SlideOutSheet>
          </div>
        </div>
      )}
    </div>
  );
}

export function ContactLtcSpecialistButton({
  featureFlags,
  iconClassName,
  buttonClassName,
  variant = 'default',
  children = 'Speak with an LTC Specialist',
}: {
  featureFlags: FeatureFlags;
  iconClassName?: string;
  buttonClassName?: string;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  children?: React.ReactNode;
}) {
  return (
    <Button
      className={buttonClassName}
      variant={variant}
      asChild
    >
      <a
        href={`${featureFlags.agis ? agisAppointmentUrl : '#'}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Headphones className={iconClassName} />
        {children}
      </a>
    </Button>
  );
}

type BenefitAmountPlan = {
  benefitAmount: number;
  premiumMonthly: number;
};

interface PlanSelectionRadioOptionsProps {
  plans: BenefitAmountPlan[];
  selectedBenefitAmount?: number;
  setSelectedBenefitAmount: (value: number) => void;
}

function PlanSelectionRadioOptions(props: PlanSelectionRadioOptionsProps) {
  const featureFlags = useSelector(selectFeatureFlags);
  return (
    <fieldset>
      <RadioGroup
        value={props.selectedBenefitAmount}
        onChange={props.setSelectedBenefitAmount}
        className="relative -space-y-px rounded-md bg-white"
      >
        {props.plans.map((plan, planIdx) => {
          const planName = formatCurrency(plan.benefitAmount);
          const premiumMonthly = formatCurrency(plan.premiumMonthly);
          const premiumMonthlyText =
            plan.premiumMonthly > 0
              ? `${premiumMonthly} / mo`
              : plan.benefitAmount === 0
                ? '$0 / mo'
                : 'Contact LTC Specialist';

          return (
            <Radio
              key={planName}
              value={plan.benefitAmount}
              aria-label={planName}
              className={classNames(
                planIdx === 0 ? 'rounded-tl-md rounded-tr-md' : '',
                planIdx === props.plans.length - 1 ? 'rounded-bl-md rounded-br-md' : '',
                'data-[checked]:border-primary-200 data-[checked]:bg-primary-100 group relative flex cursor-pointer flex-col gap-3 border border-gray-200 p-4 text-sm focus:outline-none data-[checked]:z-10 md:grid md:grid-cols-[2fr_2fr_2fr_1fr] md:gap-2 md:pl-4 md:pr-6 md:text-base',
              )}
            >
              <span className="flex items-center text-gray-900">
                <RadioButton />
                <span className="group-data-[checked]:text-primary-dark ml-3 flex  flex-col md:ml-5  md:pl-0">
                  <p className="font-semibold">{formatCurrency(plan.benefitAmount * 2)} </p>
                  <p className="text-sm">Available LTC Amount</p>
                </span>
              </span>
              <span className="ml-6 flex flex-col justify-center pl-1  text-gray-500 md:items-center  md:text-center">
                <p className="font-semibold">{planName}</p>
                <p className="text-sm">Guaranteed Death Benefit</p>
              </span>
              <span className="md:flex md:items-center md:justify-end md:text-right">
                <span className="group-data-[checked]:text-primary-dark ml-6 pl-1 font-medium group-data-[checked]:font-semibold md:ml-0 md:pl-0">
                  <span className="font-medium">{premiumMonthlyText}</span>
                </span>
              </span>
              <span className="flex items-center justify-end md:text-right">
                {plan.benefitAmount === props.selectedBenefitAmount && (
                  <InlineRowButton
                    label={plan.benefitAmount > 0 ? 'Enroll' : 'Waive'}
                    onClick={() => {}}
                    isPrimary={plan.benefitAmount > 0}
                    href={plan.benefitAmount > 0 && featureFlags.agis ? agisEnrollmentUrl : ''}
                  />
                )}
              </span>
            </Radio>
          );
        })}
      </RadioGroup>
    </fieldset>
  );
}

type InlineRowButtonProps = {
  label: string;
  onClick: () => void;
  isPrimary?: boolean;
  href?: string;
};

function InlineRowButton({ label, onClick, isPrimary, href }: InlineRowButtonProps) {
  return (
    <Button
      onClick={onClick}
      variant={isPrimary ? 'default' : 'destructive'}
      asChild
    >
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="font-medium">{label}</span>
        </a>
      ) : (
        <span className="font-medium">{label}</span>
      )}
    </Button>
  );
}

function RadioButton() {
  return (
    <span
      aria-hidden="true"
      className="group-data-[checked]:bg-primary group-data-[focus]:ring-primary flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 bg-white group-data-[checked]:border-transparent group-data-[focus]:ring-2 group-data-[focus]:ring-offset-2"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-white" />
    </span>
  );
}
